import React, { useMemo } from 'react';
import { hhmm24ToParts, partsToHhmm24, ROSTER_QUARTER_MINUTES, type Meridiem } from '../../lib/rosterTime';

export interface RosterQuarterHourTimeProps {
  value: string;
  onChange: (hhmm24: string) => void;
  /** Shown above the controls (e.g. "Start") */
  label?: string;
  className?: string;
  disabled?: boolean;
}

const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] as const;

/**
 * 12-hour time with minutes limited to :00, :15, :30, :45. Value/onChange use 24h HH:mm.
 * Non-quarter stored values are shown snapped to the nearest quarter (see lib/rosterTime).
 */
const RosterQuarterHourTime: React.FC<RosterQuarterHourTimeProps> = ({
  value,
  onChange,
  label,
  className = '',
  disabled = false,
}) => {
  const parts = useMemo(() => hhmm24ToParts(value || '00:00'), [value]);

  const setParts = (next: { hour12?: number; minute?: number; meridiem?: Meridiem }) => {
    const hour12 = next.hour12 ?? parts.hour12;
    const minute = next.minute ?? parts.minute;
    const meridiem = next.meridiem ?? parts.meridiem;
    onChange(partsToHhmm24(hour12, minute, meridiem));
  };

  return (
    <div className={className}>
      {label ? <span className="block text-[10px] font-medium text-gray-500 uppercase mb-0.5">{label}</span> : null}
      <div className="flex flex-wrap items-center gap-1">
        <select
          aria-label={label ? `${label} hour` : 'Hour'}
          value={parts.hour12}
          disabled={disabled}
          onChange={(e) => setParts({ hour12: Number(e.target.value) })}
          className="min-w-[3rem] border border-gray-300 rounded px-1 py-0.5 text-[11px] bg-white text-gray-900 disabled:opacity-50"
        >
          {HOURS_12.map((h) => (
            <option key={h} value={h}>
              {h}
            </option>
          ))}
        </select>
        <span className="text-gray-400 text-[11px]">:</span>
        <select
          aria-label={label ? `${label} minutes` : 'Minutes'}
          value={parts.minute}
          disabled={disabled}
          onChange={(e) => setParts({ minute: Number(e.target.value) })}
          className="min-w-[3rem] border border-gray-300 rounded px-1 py-0.5 text-[11px] bg-white text-gray-900 disabled:opacity-50"
        >
          {ROSTER_QUARTER_MINUTES.map((m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, '0')}
            </option>
          ))}
        </select>
        <select
          aria-label={label ? `${label} AM or PM` : 'AM or PM'}
          value={parts.meridiem}
          disabled={disabled}
          onChange={(e) => setParts({ meridiem: e.target.value as Meridiem })}
          className="min-w-[3.25rem] border border-gray-300 rounded px-1 py-0.5 text-[11px] bg-white text-gray-900 disabled:opacity-50"
        >
          <option value="am">am</option>
          <option value="pm">pm</option>
        </select>
      </div>
    </div>
  );
};

export default RosterQuarterHourTime;
